import * as React from 'react'
import { ReactElement } from 'react'
import { Icon, IIconProps, ImageCoverStyle, ImageFit } from '@fluentui/react'
import { DocumentIconNameMap, DocumentIconSource, DocumentIconSourceMap } from '../config/documentConfig'
import { FileTypeIconSize, getFileTypeIconProps } from '@fluentui/react-file-type-icons'
import { PartyType } from '../components/Form/Party/PartyForm'
import { DocumentType, DocumentTypeMap, getDocumentExtension } from '../helpers/documentHelper'
import { isPartyOverviewItem, ListItemDto, PartyOverviewItemDto } from '../config/partyConfig'
import { BuildingRegular, PersonRegular, ShieldPersonRegular } from '@fluentui/react-icons'

export const getPartyIconName = (party: ListItemDto): string => {
    if (isPartyOverviewItem(party) && (party as PartyOverviewItemDto).identificationVerified) {
        return 'ContactCard'
    }

    switch (party.type) {
        case PartyType.Legal: {
            return 'EMI'
        }
        case PartyType.Natural: {
            return 'Contact'
        }
    }

    return undefined
}

export const getPartyIcon = (party: ListItemDto, props?: React.SVGAttributes<SVGElement>): React.JSX.Element => {
    if (isPartyOverviewItem(party) && (party as PartyOverviewItemDto).identificationVerified) {
        return <ShieldPersonRegular />
    }

    switch (party.type) {
        case PartyType.Legal: {
            return <BuildingRegular {...props} />
        }
        case PartyType.Natural: {
            return <PersonRegular {...props} />
        }
    }

    return undefined
}

export enum CustomIconSrc {
    Hammock = 'icons/icon-80.png',
    AddNote = 'icons/add-note-icon-80.png',
    AddTask = 'icons/add-task-icon-80.png',
    AddTime = 'icons/add-time-icon-80.png',
    SearchFile = 'icons/search-file-icon-80.png',
    SearchParty = 'icons/search-party-icon-80.png',
}

export function getHammockIconProps(): IIconProps {
    return {
        imageProps: {
            src: CustomIconSrc.Hammock,
            height: 16,
            width: 16,
            imageFit: ImageFit.centerCover,
            coverStyle: ImageCoverStyle.landscape,
        },
    }
}

export function getApplicationIconPropsByType(type: DocumentType, size: FileTypeIconSize = 16): IIconProps {
    const iconName = DocumentIconNameMap.get(type)

    switch (DocumentIconSourceMap.get(type)) {
        case DocumentIconSource.Icon:
            return { iconName }
        case DocumentIconSource.Image:
            return {
                imageProps: {
                    src: `icons/${iconName}`,
                    height: size,
                    width: size,
                    imageFit: ImageFit.contain,
                    coverStyle: ImageCoverStyle.landscape,
                },
            }
        case DocumentIconSource.Svg:
            return {
                imageProps: {
                    src: `icons/${iconName}.svg`,
                    height: size,
                    width: size,
                    imageFit: ImageFit.contain,
                    coverStyle: ImageCoverStyle.landscape,
                },
            }
        default:
            return iconName && getFileTypeIconProps({ extension: iconName, size, imageFileType: 'svg' })
    }
}

/**
 * Get the SVG icon that belongs to an office application.
 */
export function getApplicationIconPropsByFileName(fileName: string, size: FileTypeIconSize = 16): IIconProps {
    const extension = getDocumentExtension(fileName)
    const type = DocumentTypeMap.get(extension)
    return getApplicationIconPropsByType(type, size) || getFileTypeIconProps({ extension, size, imageFileType: 'svg' })
}

export function getInlineTextFromNode(text?: ReactElement, icon?: ReactElement, props?): ReactElement {
    return (
        <span className="document-name" {...props}>
            {icon && <span className="document-name-icon">{icon}</span>}
            {text && <span className="document-name-text">{text}</span>}
        </span>
    )
}

export function getInlineTextFromString(text?: string, icon?: string, props?): ReactElement {
    return getInlineTextFromNode(text ? <>{text}</> : null, icon ? <Icon iconName={icon} /> : null, props)
}
